import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { AdministrationComponent } from './administration/administration.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { HomeComponent } from './home/home.component';
import { LoginComponent } from './home/login/login.component';
import { NewRegistrationComponent } from './home/new-registration/new-registration.component';
import { HeaderNavigationComponent } from './common/header-navigation/header-navigation.component';
import { ErrorComponent } from './common/error/error.component';
import { RegistrationComponent } from './registration/registration.component';
import { BasicProfileComponent } from './registration/basic-profile/basic-profile.component';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { EmployeeOverviewComponent } from './employee-overview/employee-overview.component';
import { DetailsFormComponent } from './employee-overview/details-form/details-form.component';
import {
  BreadcrumbModule,
  ButtonModule,
  CalendarModule,
  CardModule,
  CheckboxModule,
  ConfirmDialogModule,
  DialogModule,
  DropdownModule,
  FileUploadModule,
  GMapModule,
  InputNumberModule,
  InputSwitchModule,
  InputTextareaModule,
  InputTextModule,
  MenuModule,
  MessageService,
  MessagesModule,
  MultiSelectModule,
  PasswordModule,
  ProgressSpinnerModule,
  RadioButtonModule,
  StepsModule,
  TableModule,
  TabViewModule,
  ToastModule,
  TooltipModule,
  InputMaskModule,
} from 'primeng';
import { DataComponent } from './data/data.component';
import { DocumentsComponent } from './documents/documents.component';
import { MessagesComponent } from './messages/messages.component';
import { NavigationComponent } from './navigation/navigation.component';
import { FileComponent } from './registration/file/file.component';
import { NestedFileComponent } from './registration/nested-file/nested-file.component';
import { RegistrationService } from './registration/registration.service';
import { CommunicationService } from './services/communication.service';
import { ErrorService } from './services/error/error.service';
import { LocalStorageService } from './services/local-storage.service';
import { LoginGuardService } from './services/login-guard.service';
import { TokenInterceptor } from './services/token-interceptor';
import { UserService } from './services/user.service';
import { FileService } from './services/file.service';
import { LoggerService } from './services/logger.service';
import { SuccessfulRegistrationComponent } from './successful-registration/successful-registration.component';
import { GuestComponent } from './guest/guest.component';
import { GuestProfileComponent } from './guest/guest-profile/guest-profile.component';
import { RedirectComponent } from './redirect/redirect.component';
import { LicenseComponent } from './license/license.component';
import { NotificationsComponent } from './common/notifications/notifications.component';
import { NotificationsService } from './services/notifications/notifications.service';
import { PanelComponent } from './panel/panel.component';
import { JurisdictionComponent } from './jurisdiction/jurisdiction.component';
import { LocationFormComponent } from './jurisdiction/location-form/location-form.component';
import { PersonalDataComponent } from './jurisdiction/personal-data/personal-data.component';
import { FacilitiesFormComponent } from './jurisdiction/facilities-form/facilities-form.component';
import { MeterDataComponent } from './jurisdiction/facilities-form/meter-data/meter-data.component';
import { OldMeterDataComponent } from './jurisdiction/facilities-form/old-meter-data/old-meter-data.component';
import { BaseDataComponent } from './jurisdiction/facilities-form/base-data/base-data.component';
import { SpecialApplianceComponent } from './jurisdiction/facilities-form/base-data/special-appliance/special-appliance.component';
import { NewFacilityComponent } from './jurisdiction/facilities-form/base-data/new-facility/new-facility.component';
import { GasDeviceComponent } from './jurisdiction/facilities-form/base-data/gas-device/gas-device.component';
import { WaterDeviceComponent } from './jurisdiction/facilities-form/base-data/water-device/water-device.component';
import { DistrictHeatDeviceComponent } from './jurisdiction/facilities-form/base-data/district-heat-device/district-heat-device.component';
import { JurisdictionOverviewComponent } from './jurisdiction-overview/jurisdiction-overview.component';
import { AptMapComponent } from './apt-map/apt-map.component';
import { NetzTranslationLoader } from './services/netz-translation-loader';
import { NavigationStateShareService } from './services/navigation-state-share.service';
import { NumberTypeDirective } from './directives/number-type.directive';
import { OrderProcessComponent } from './jurisdiction/order-process/order-process.component';
import { SubscriberComponent } from './jurisdiction/personal-data/subscriber/subscriber.component';
import { ConnectionAddressComponent } from './jurisdiction/personal-data/connection-address/connection-address.component';
import { ResponsibleInstallerComponent } from './jurisdiction/personal-data/responsible-installer/responsible-installer.component';
import { ProcessDetailsComponent } from './jurisdiction/process-details/process-details.component';
import { InstallerCompanyComponent } from './jurisdiction/personal-data/installer-company/installer-company.component';
import { TechnicalDataComponent } from './jurisdiction/technical-data/technical-data.component';
import { TechnicianDataComponent } from './jurisdiction/technician-data/technician-data.component';
import { TechnicianBaseDataComponent } from './jurisdiction/technician-data/base-data/base-data.component';
import { TechnicianGasConnectionComponent } from './jurisdiction/technician-data/gas-connection/gas-connection.component';
import { TechnicianHeatingConnectionComponent } from './jurisdiction/technician-data/heating-connection/heating-connection.component';
import { TechnicianPowerConnectionComponent } from './jurisdiction/technician-data/power-connection/power-connection.component';
import { TechnicianWaterConnectionComponent } from './jurisdiction/technician-data/water-connection/water-connection.component';
import { JurisdictionService } from './jurisdiction/jurisdiction.service';
import { SpecialConsumersComponent } from './jurisdiction/technician-data/power-connection/special-consumers/special-consumers.component';
import { CommonService } from './services/common.service';
import { ResetPasswordConfirmComponent } from './home/reset-password-confirm/reset-password-confirm.component';
import { OrderProcessDocumentsComponent } from './jurisdiction/order-process-documents/order-process-documents.component';
import { InvoiceAddressComponent } from './jurisdiction/personal-data/invoice-address/invoice-address.component';
import { SubmittedComponent } from './submitted/submitted.component';
import { ImpressumComponent } from './impressum/impressum.component';
import { NewAdditionsComponent } from './new-additions/new-additions.component';
import { DownloadCenterComponent } from './download-center/download-center.component';
import { DownloadCenterService } from './services/download-center.service';
import { FloatNumberTypeDirective } from './directives/float-number-type.directive';
import { AlphaNumericTypeDirective } from './directives/alpha-numeric-type.directive';
import { CookieService } from 'ngx-cookie-service';
import { StreetNumberDirective } from './directives/street-number.directive';
import { AlphabetTypeDirective } from './directives/alphabet-type.directive';
import { MediaSelectionFormComponent } from './jurisdiction/media-selection-form/media-selection-form.component';
import { EnvironmentService } from './services/environment.service';
import { AlphaNumericSpecialDirective } from './directives/alpha-numeric-special.directive';
import { ChangePasswordDialogComponent } from './home/change-password/change-password.component';
import { ConnectionLocationFormatPipe } from './pipes/connection-location-format.pipe';
import { AreaFieldFieldSectorPipe } from './pipes/area-field-field-sector-format.pipe';
import { AddDistrictPipe } from './pipes/district.pipe';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { SiteElectricityInstallationTechnicalDataComponent } from './jurisdiction/siteelectricity-installation-technical-data/siteelectricity-installation-technical-data.component';

import { TechnicalDataHeatingComponent } from './jurisdiction/technical-data-heating/technical-data-heating.component';
import { InformationOnConstructionComponent } from './jurisdiction/technical-data-heating/information-on-construction/information-on-construction.component';
import { ExhaustSystemDataComponent } from './jurisdiction/technical-data-heating/exhaust-system-data/exhaust-system-data.component';
import { FireplaceVentilationSystemComponent } from './jurisdiction/technical-data-heating/fireplace-ventilation-system/fireplace-ventilation-system.component';
import { ReleasesComponent } from './jurisdiction/technical-data-heating/releases/releases.component';
import { InformationOnMeterInstallationComponent } from './jurisdiction/technical-data-heating/information-on-meter-installation/information-on-meter-installation.component';

import { ApiService } from './services/api.service';
import { GridCompatibilityModule } from './+grid-compatibility/grid-compatibility.module';
import { SharedModule } from './+shared/shared.module';
import { FacilitiesDataComponent } from './jurisdiction/facilities-data/facilities-data.component';
import { WaterSystemComponentsComponent } from './jurisdiction/facilities-form/base-data/water-system-components/water-system-components.component';
import { LocationService } from './jurisdiction/location-form/location.service';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { ConcessionBasicProfileComponent } from './concession-registration/concession-basic-profile/concession-basic-profile.component';
import { DataConcessionValidationComponent } from './concession-registration/concession-basic-profile/data-concession-validation/data-concession-validation.component';
import { RegepModule } from './+regep/regep.module';
import { RegepJurisdictionComponent } from './+regep/components/regep-jurisdiction/regep-jurisdiction.component';
import { ConcessionCompanyDataComponent } from './concession-registration/concession-basic-profile/concession-company-data/concession-company-data.component';
import { ConcessionTechnicianDataComponent } from './concession-registration/concession-basic-profile/concession-technician-data/concession-technician-data.component';
import { DataGopComponent } from './data-gop/data-gop.component';
import { ConcessionFilesComponent } from './concession-registration/concession-basic-profile/concession-files/concession-files.component';
import { LicenseService } from './license/license.service';
import { InviteRegistrationComponent } from './invite-registration/invite-registration.component';
import { InviteRegistrationService } from './invite-registration/invite-registration.service';
import { DisplayTermsComponent } from './home/new-registration/display-terms/display-terms.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { LocationFieldsComponent } from './jurisdiction/personal-data/location-fields/location-fields.component';
import { ConnectionTypeComponent } from './jurisdiction/connection-type/connection-type.component';
import { CopyApplicationDataComponent } from './jurisdiction/copy-application-data/copy-application-data.component';
import { CopyApplicationDataService } from './jurisdiction/copy-application-data/copy-application-data.service';
import { DrrCcdDevicesJurisdictionComponent } from './jurisdiction/drr-ccd-devices-jurisdiction/drr-ccd-devices-jurisdiction.component';
import { DrrCcdDevicesModule } from './+drr-ccd-devices/drr-ccd-devices.module';

export function api_init(apiService: ApiService) {
  return () => apiService.initialize();
}

@NgModule({
  declarations: [
    AdministrationComponent,
    AlphabetTypeDirective,
    AlphaNumericSpecialDirective,
    AlphaNumericTypeDirective,
    AppComponent,
    BaseDataComponent,
    BasicProfileComponent,
    ChangePasswordComponent,
    ChangePasswordDialogComponent,
    ConnectionAddressComponent,
    DashboardComponent,
    DataComponent,
    DetailsFormComponent,
    DocumentsComponent,
    EmployeeOverviewComponent,
    ErrorComponent,
    FacilitiesFormComponent,
    GasDeviceComponent,
    HeaderNavigationComponent,
    HomeComponent,
    LoginComponent,
    MediaSelectionFormComponent,
    MessagesComponent,
    NavigationComponent,
    NewRegistrationComponent,
    StreetNumberDirective,
    OrderProcessComponent,
    OrderProcessDocumentsComponent,
    PersonalDataComponent,
    PrivacyPolicyComponent,
    ProcessDetailsComponent,
    RegistrationComponent,
    ResponsibleInstallerComponent,
    SiteElectricityInstallationTechnicalDataComponent,
    SpecialApplianceComponent,
    SpecialConsumersComponent,
    SubscriberComponent,
    SuccessfulRegistrationComponent,
    TechnicianBaseDataComponent,
    TechnicianDataComponent,
    TechnicianGasConnectionComponent,
    TechnicianHeatingConnectionComponent,
    TechnicianPowerConnectionComponent,
    TechnicianWaterConnectionComponent,
    FileComponent,
    NestedFileComponent,
    NewFacilityComponent,
    NotificationsComponent,
    GuestComponent,
    GuestProfileComponent,
    RedirectComponent,
    LicenseComponent,
    PanelComponent,
    JurisdictionComponent,
    LocationFormComponent,
    NumberTypeDirective,
    FloatNumberTypeDirective,
    MeterDataComponent,
    WaterDeviceComponent,
    DistrictHeatDeviceComponent,
    OldMeterDataComponent,
    JurisdictionOverviewComponent,
    AptMapComponent,
    InstallerCompanyComponent,
    TechnicalDataComponent,
    ResetPasswordConfirmComponent,
    InvoiceAddressComponent,
    SubmittedComponent,
    ImpressumComponent,
    NewAdditionsComponent,
    DownloadCenterComponent,
    ConnectionLocationFormatPipe,
    AreaFieldFieldSectorPipe,
    AddDistrictPipe,
    TechnicalDataHeatingComponent,
    InformationOnConstructionComponent,
    ExhaustSystemDataComponent,
    FireplaceVentilationSystemComponent,
    ReleasesComponent,
    InformationOnMeterInstallationComponent,
    FacilitiesDataComponent,
    WaterSystemComponentsComponent,
    DataConcessionValidationComponent,
    ConcessionBasicProfileComponent,
    RegepJurisdictionComponent,
    ConcessionCompanyDataComponent,
    ConcessionTechnicianDataComponent,
    DataGopComponent,
    ConcessionFilesComponent,
    InviteRegistrationComponent,
    DisplayTermsComponent,
    ResetPasswordComponent,
    LocationFieldsComponent,
    ConnectionTypeComponent,
    CopyApplicationDataComponent,
    DrrCcdDevicesJurisdictionComponent,
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ConfirmDialogModule,
    AppRoutingModule,
    BreadcrumbModule,
    BrowserAnimationsModule,
    BrowserModule,
    TooltipModule,
    ButtonModule,
    DropdownModule,
    CalendarModule,
    CheckboxModule,
    DialogModule,
    FileUploadModule,
    HttpClientModule,
    InputTextareaModule,
    RadioButtonModule,
    InputTextModule,
    MenuModule,
    MultiSelectModule,
    StepsModule,
    TabViewModule,
    TableModule,
    ToastModule,
    GMapModule,
    InputSwitchModule,
    PasswordModule,
    ProgressSpinnerModule,
    MessagesModule,
    CardModule,
    DrrCcdDevicesModule,
    InputMaskModule,
    QuillModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: NetzTranslationLoader,
        deps: [HttpClient, EnvironmentService],
      },
    }),
    InputNumberModule,
    SharedModule,
    GridCompatibilityModule,
    RegepModule,
  ],
  providers: [
    NgForm,
    CookieService,
    CommonService,
    CommunicationService,
    ApiService,
    EnvironmentService,
    ErrorService,
    FileService,
    JurisdictionService,
    LocalStorageService,
    LoginGuardService,
    LoggerService,
    MessageService,
    NavigationStateShareService,
    NotificationsService,
    RegistrationService,
    UserService,
    DownloadCenterService,
    LicenseService,
    InviteRegistrationService,
    CopyApplicationDataService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: api_init,
      deps: [ApiService],
      multi: true,
    },
    LocationService,
    // {
    //   provide: APP_BASE_HREF,
    //   useValue: '/' + (window.location.pathname.split('/')[1] || ''),
    // },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
